/**
 * InnerLink
 * ページ内リンクのスムーススクロール（ヘッダー高さ考慮）
 * Switcherで管理されているハッシュは除外
 */
export default class InnerLink {
  constructor(options = {}) {
    this.options = {
      selector: 'a[href^="#"]',
      duration: 500,
      easing: 'ease-in-out',
      headerSelector: '.l-header',
      offset: 20, // ヘッダー下の余白
      excludeSelector: '.js-switcher-trigger', // Switcherトリガーのみ除外
      enableCrossPagesScroll: false, // 別ページからの遷移時のスクロール
      ...options
    };

    this.init();
  }

  init() {
    // Switcherで管理されているハッシュを収集
    this.switcherHashes = this.collectSwitcherHashes();
    
    // mv-page付きSwitcherの存在を確認
    this.hasMvPageSwitcher = document.querySelector('.js-switcher.mv-page') !== null;
    
    // イベント設定
    this.setupEvents();
  }

  collectSwitcherHashes() {
    const hashes = new Set();
    const switcherLinks = document.querySelectorAll(this.options.excludeSelector);
    
    switcherLinks.forEach(link => {
      const hash = link.getAttribute('href');
      if (hash && hash.startsWith('#')) {
        hashes.add(hash.substring(1));
      }
    });
    
    return hashes;
  }

  setupEvents() {
    // クリックイベント
    document.addEventListener('click', this.handleClick.bind(this));
    
    // ページロード時のハッシュ処理（ページ遷移時も含む）
    this.handleInitialHash();
    
    // hashchangeイベント（戻る/進むボタン対応）
    window.addEventListener('hashchange', this.handleHashChange.bind(this));
  }

  handleInitialHash() {
    if (window.location.hash) {
      const hash = window.location.hash.substring(1);
      
      // mv-page付きSwitcherのハッシュの場合はトップに戻す
      if (this.hasMvPageSwitcher && this.isSwitcherHash(hash)) {
        // ページ読み込み完了後にトップへ
        if (document.readyState === 'complete') {
          window.scrollTo(0, 0);
        } else {
          window.addEventListener('load', () => {
            window.scrollTo(0, 0);
          });
        }
        return;
      }
      
      // Switcher以外のハッシュ、または通常のSwitcherハッシュは通常処理
      if (!this.isSwitcherHash(hash)) {
        // ページ遷移直後の場合、DOMの構築を待つ
        if (document.readyState === 'complete') {
          this.scrollToHashWithDelay();
        } else {
          window.addEventListener('load', () => {
            this.scrollToHashWithDelay();
          });
        }
      }
    }
  }

  scrollToHashWithDelay() {
    // 少し遅延を入れて確実に要素が配置された後にスクロール
    setTimeout(() => {
      // 一旦トップに戻す（ブラウザのデフォルト動作をキャンセル）
      window.scrollTo(0, 0);
      
      // スムーススクロールするかどうか
      const smooth = this.options.enableCrossPagesScroll;
      
      // ヘッダー高さを考慮してスクロール
      this.scrollToHash(window.location.hash, smooth);
    }, 50);
  }

  handleHashChange() {
    const hash = window.location.hash;
    if (hash && !this.isSwitcherHash(hash.substring(1))) {
      this.scrollToHash(hash);
    }
  }

  handleClick(e) {
    const link = e.target.closest(this.options.selector);
    
    if (!link) return;
    
    // Switcher内のリンクは除外
    if (link.matches(this.options.excludeSelector)) return;
    
    const hash = link.getAttribute('href');
    
    // ハッシュのみのリンクでない場合はスキップ
    if (!hash || !hash.startsWith('#') || hash === '#') return;
    
    // Switcherで管理されているハッシュはスキップ
    if (this.isSwitcherHash(hash.substring(1))) return;
    
    e.preventDefault();
    
    // スクロール実行
    this.scrollToHash(hash);
    
    // URLにハッシュを追加（履歴に残す）
    if (history.pushState) {
      history.pushState(null, null, hash);
    }
  }

  isSwitcherHash(hash) {
    return this.switcherHashes.has(hash);
  }

  scrollToHash(hash, smooth = true) {
    // 無効なセレクタをチェック
    if (!this.isValidSelector(hash)) {
      console.warn(`Invalid selector: ${hash}`);
      return;
    }
    
    let target;
    try {
      target = document.querySelector(hash);
    } catch (e) {
      console.warn(`Failed to query selector: ${hash}`, e);
      return;
    }
    
    if (!target) return;
    
    const targetPosition = this.getTargetPosition(target);
    
    if (smooth) {
      this.smoothScroll(targetPosition);
    } else {
      // 即座にスクロール（ページ遷移時など）
      window.scrollTo(0, targetPosition);
    }
  }

  isValidSelector(selector) {
    // セレクタの基本的な妥当性をチェック
    if (!selector || typeof selector !== 'string') return false;
    
    // ハッシュで始まらない場合は無効
    if (!selector.startsWith('#')) return false;
    
    // IDが数字で始まる場合は無効
    const id = selector.substring(1);
    if (/^\d/.test(id)) return false;
    
    // 空のIDは無効
    if (id.length === 0) return false;
    
    return true;
  }

  getTargetPosition(target) {
    const header = document.querySelector(this.options.headerSelector);
    const headerHeight = header ? header.offsetHeight : 0;
    const targetTop = target.getBoundingClientRect().top + window.pageYOffset;
    
    return targetTop - headerHeight - this.options.offset;
  }

  smoothScroll(targetPosition) {
    const startPosition = window.pageYOffset;
    const distance = targetPosition - startPosition;
    const startTime = performance.now();
    
    const animation = (currentTime) => {
      const elapsedTime = currentTime - startTime;
      const progress = Math.min(elapsedTime / this.options.duration, 1);
      
      // イージング計算
      const ease = this.easeInOutCubic(progress);
      
      window.scrollTo(0, startPosition + distance * ease);
      
      if (progress < 1) {
        requestAnimationFrame(animation);
      }
    };
    
    requestAnimationFrame(animation);
  }

  easeInOutCubic(t) {
    return t < 0.5 
      ? 4 * t * t * t 
      : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  // 外部から特定の要素へスクロール
  scrollToElement(element) {
    if (typeof element === 'string') {
      element = document.querySelector(element);
    }
    
    if (!element) return;
    
    const targetPosition = this.getTargetPosition(element);
    this.smoothScroll(targetPosition);
  }

  // Switcherハッシュリストを更新（動的に追加された場合用）
  updateSwitcherHashes() {
    this.switcherHashes = this.collectSwitcherHashes();
  }
}